// Testador por tipo do Playground oficial: os limites da Meta e o que ela faria
// com cada mensagem. Separado do componente (PlaygroundTester.tsx) para o fast
// refresh do Vite e para os testes.

// Limites da Cloud API (apps/api/internal/metaclient/messages.go). Acima deles a
// API não erra: botões e lista viram menu de texto numerado.
export const META_LIMITS = {
  text: 4096, caption: 1024, body: 1024, buttons: 3, buttonTitle: 20,
  listRows: 10, listButton: 20, rowTitle: 24, rowDesc: 72, ctaButton: 20, header: 60,
} as const;

/** Tipos que o testador sabe montar e mostrar. */
export const TESTER_TYPES = ['text', 'image', 'video', 'document', 'audio', 'sticker', 'location', 'contact', 'reaction', 'buttons', 'list', 'ctaUrl', 'locationRequest'] as const;
export type TesterType = typeof TESTER_TYPES[number];
export const isTesterType = (id: string): id is TesterType => (TESTER_TYPES as readonly string[]).includes(id);

export type Body = Record<string, unknown>;
export interface Media { url?: string; caption?: string; filename?: string; ptt?: boolean }
export interface Btn { id?: string; title?: string }
export interface Row { id?: string; title?: string; description?: string }
export interface Section { title?: string; rows?: Row[] }

export const runes = (s: unknown) => [...String(s ?? '')].length;
export const str = (v: unknown) => (v == null ? '' : String(v));

export function parseBody(payload: string): Body {
  try { const o = JSON.parse(payload); return o && typeof o === 'object' ? o as Body : {}; } catch { return {}; }
}

/** O que a Meta faria com esta mensagem — o que vale avisar antes de enviar. */
export function metaNotes(type: string, body: Body): string[] {
  const media = (body.media ?? {}) as Media;
  const notes: string[] = [];
  if ((type === 'audio' || type === 'sticker') && media.caption) notes.push('captionDropped');
  if (type === 'text' && runes(body.body) > META_LIMITS.text) notes.push('textTooLong');
  if (['image', 'video', 'document'].includes(type) && runes(media.caption) > META_LIMITS.caption) notes.push('captionTooLong');
  if (type === 'buttons') {
    const bs = (body.buttons ?? []) as Btn[];
    if (bs.length === 0 || bs.length > META_LIMITS.buttons || bs.some((b) => !b.title || runes(b.title) > META_LIMITS.buttonTitle)
      || runes(body.body) > META_LIMITS.body) notes.push('buttonsAsMenu');
  }
  if (type === 'list') {
    const rows = ((body.sections ?? []) as Section[]).flatMap((s) => s.rows ?? []);
    if (rows.length === 0 || rows.length > META_LIMITS.listRows || runes(body.button_text) > META_LIMITS.listButton
      || rows.some((r) => !r.title || runes(r.title) > META_LIMITS.rowTitle || runes(r.description) > META_LIMITS.rowDesc)
      || runes(body.body) > META_LIMITS.body) notes.push('listAsMenu');
  }
  if (type === 'reaction' && !body.quoted_message_id) notes.push('reactionNeedsMessage');
  if (type === 'ctaUrl') {
    if (body.url && !/^https?:\/\//.test(str(body.url))) notes.push('ctaUrlInvalid');
    if (runes(body.button_text) > META_LIMITS.ctaButton) notes.push('ctaButtonTooLong');
  }
  const header = (body.header ?? {}) as { type?: string; text?: string };
  if ((type === 'buttons' || type === 'list' || type === 'ctaUrl') && runes(header.text) > META_LIMITS.header) notes.push('headerTooLong');
  return notes;
}

// Menu de texto numerado — o que sai quando botões ou lista passam do limite da
// Meta. Mesmo formato de RenderButtonsMenu/RenderListMenu (message/fallback.go).
export function buttonsMenu(body: Body, bs: Btn[]) {
  let out = str(body.body).trim() + '\n';
  bs.forEach((b, i) => { out += `\n${i + 1}. ${str(b.title)}`; });
  if (body.footer) out += `\n\n${str(body.footer)}`;
  return out;
}
export function listMenu(body: Body, sections: Section[]) {
  let out = str(body.body).trim() + '\n';
  let n = 0;
  for (const sec of sections) {
    if (sec.title) out += `\n*${sec.title}*`;
    for (const r of sec.rows ?? []) {
      n++;
      out += `\n${n}. ${str(r.title)}`;
      if (r.description) out += ` — ${r.description}`;
    }
  }
  if (body.footer) out += `\n\n${str(body.footer)}`;
  return out;
}
